import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Divider, FormLabel, Grid, IconButton, Stack, Toolbar, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';
import { EditableTextField } from "../Shared/FormItems";
import { ChangeProps } from "../Lessons/Show";
import { Solution } from "../../types/Solutions";
import Linkify from "linkify-react";

const Summary = (props: { solution: Solution, editable?: boolean } & ChangeProps ) => {

  const [copyLink, shouldCopyLink] = useState<boolean>(false);
  const [editSummary, setEditSummary] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<boolean>(false);

  useEffect(() => {
    if (typeof(props.solution) === "undefined" || !copyLink || !props.solution.url) { return }
    navigator.clipboard.writeText(props.solution.url);
  }, [copyLink]);

  const shareDialog = () => {
    if (typeof(props.solution) === "undefined") { return }
    if (typeof(navigator.share) !== "undefined") {
      navigator.share({url: props.solution.url}).then(fulfilled => {
        if (typeof(fulfilled) === "undefined") {
          shouldCopyLink(true);
        }
      }).catch(rejected => shouldCopyLink(true));
      return;
    }
  }

  return <Grid container spacing={3}>
    <Grid size={12}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <FormLabel>Solution from {dayjs.unix(props.solution.created_at).format("ll")} { props.solution.resolved_at && <> / Resolved at: {dayjs.unix(props.solution.resolved_at).format("lll")}</> }</FormLabel>

          <Stack direction="row" alignItems="center" gap={0.5}>
            { editTitle ? <EditableTextField
                                     placeholder="Enter a title for the solution"
                                     rows={1}
                                     onChange={value => props.onChange({issue: value})}
                                     onSave={value => {
                                        props.onSave({issue: value});
                                        setEditTitle(false);
                                     }}
                                     defaultValue={props.solution.issue}
                                     fullWidth /> : <h3 style={{marginBottom: 0}}>{props.solution.issue}</h3> }
            { props.editable && <IconButton color="info" size="small" onClick={() => setEditTitle(true)}><EditIcon fontSize="small" /></IconButton> }
          </Stack>
        </Box>

        <Toolbar disableGutters>
          <Tooltip 
            title={`Link copied`} 
            open={copyLink}>
              <Button
                size="small"
                variant="text"
                color="secondary"
                startIcon={<ShareIcon />}
                onClick={shareDialog}>Share</Button>
          </Tooltip>

        </Toolbar>
      </Stack>
      <Divider sx={{mt: 1}} />
    </Grid>

    <Grid size={{xs: 12}}>
      <Stack direction="row" alignItems="center" gap={0.5} mb={2}>
        <strong>Executive summary</strong>
        { props.editable && <IconButton color="info" size="small" onClick={() => setEditSummary(true)}><EditIcon fontSize="small" /></IconButton> }
      </Stack>
      
      { editSummary ? <EditableTextField placeholder="Summarize the issue in a few sentences."
                          defaultValue={props.solution.summary}
                          rows={10}
                          onChange={value => props.onChange({summary: value})}
                          onSave={value => {
                            props.onSave({summary: value});
                            setEditSummary(false);
                          }} /> : <p>{props.solution.summary}</p> }
    </Grid>

    <Grid size={{xs: 12}}>
      { props.solution.measures && <>
        <strong>Measures</strong>
        <p>{Object.entries(props.solution.measures).map(([key, value]) => `${key}: ${value}`).join("\n")}</p>
      </> }
    </Grid>

    <Grid size={{xs: 12}}>
      { props.solution.mitigation && <>
        <strong>Mitigation</strong>
        <Alert sx={{mt: 1}}><Linkify options={{target: "_blank"}}>{props.solution.mitigation}</Linkify></Alert>
      </> }
    </Grid>
    
  </Grid>
}

export default Summary;